import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import ProductionMaterialWorkflow from '../components/ProductionMaterialWorkflow';

const STATUS_COLORS = {
  pending:   { bg:'#FFF3DC', color:'#8B5E0A' },
  approved:  { bg:'#E8F5EB', color:'#2E6B3A' },
  rejected:  { bg:'#FDEAEA', color:'#8F2E24' },
  completed: { bg:'#EDF4FF', color:'#1E4FAD' },
};

const PRIORITIES = ['low','normal','high','urgent'];
const PAYMENT_SOURCES = ['Petty Cash','Cash on Hand','UBA','Moniepoint Main','Kuda MFB'];
const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG');

export default function Requisitions() {
  const { user } = useAuth();
  const isSuperAdmin =
    user?.role === 'super_admin';

  const productionAccess = [
    'super_admin',
    'production_team_lead',
    'inventory_officer'
  ].includes(user?.role);

  const defaultWorkflow =
    [
      'production_team_lead',
      'inventory_officer'
    ].includes(user?.role)
      ? 'production'
      : 'general';

  const [workflow, setWorkflow] =
    useState(defaultWorkflow);

  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] =
    useState(true);
  const [showForm, setShowForm] =
    useState(false);
  const [selected, setSelected] =
    useState(null);
  const [activeTab, setActiveTab] =
    useState('all');

  const load = () => {
    setLoading(true);

    get('/requisitions')
      .then(data =>
        setReqs(data.data || [])
      )
      .finally(() =>
        setLoading(false)
      );
  };

  useEffect(() => {
    load();
  }, []);

  const filtered =
    reqs.filter(
      request =>
        activeTab === 'all' ||
        request.status === activeTab
    );

  const pendingCount =
    reqs.filter(
      request =>
        request.status === 'pending'
    ).length;

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">
            📋 Requisitions
          </h2>

          <p
            className="text-muted text-sm"
            style={{marginTop: 4}}
          >
            General requests and controlled
            production material requisitions
          </p>
        </div>

        {workflow === 'general' && (
          <button
            className="btn btn-primary"
            onClick={() =>
              setShowForm(true)
            }
          >
            + New Requisition
          </button>
        )}
      </div>

      {productionAccess && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 20,
            padding: 5,
            background: '#F2EFE7',
            borderRadius: 10,
            width: 'fit-content'
          }}
        >
          <button
            className={
              'btn btn-sm ' +
              (
                workflow === 'general'
                  ? 'btn-primary'
                  : 'btn-ghost'
              )
            }
            onClick={() =>
              setWorkflow('general')
            }
          >
            General Requisitions
          </button>

          <button
            className={
              'btn btn-sm ' +
              (
                workflow === 'production'
                  ? 'btn-primary'
                  : 'btn-ghost'
              )
            }
            onClick={() =>
              setWorkflow('production')
            }
          >
            Production Materials
          </button>
        </div>
      )}

      {workflow === 'production' &&
      productionAccess ? (
        <ProductionMaterialWorkflow
          user={user}
        />
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              gap: 6,
              marginBottom: 20,
              borderBottom:
                '1px solid var(--border-light)',
              paddingBottom: 12,
              flexWrap: 'wrap'
            }}
          >
            {[
              ['all', 'All'],
              ['pending', 'Pending'],
              ['approved', 'Approved'],
              ['completed', 'Completed'],
              ['rejected', 'Rejected']
            ].map(([key, label]) => (
              <button
                key={key}
                className={
                  'btn btn-sm ' +
                  (
                    activeTab === key
                      ? 'btn-primary'
                      : 'btn-ghost'
                  )
                }
                onClick={() =>
                  setActiveTab(key)
                }
              >
                {label}

                {key === 'pending' &&
                  pendingCount > 0 && (
                    <span
                      style={{
                        marginLeft: 6,
                        background: '#8F2E24',
                        color: '#fff',
                        borderRadius: 10,
                        padding: '0 6px',
                        fontSize: 10
                      }}
                    >
                      {pendingCount}
                    </span>
                  )}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="spinner"></div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <h3>No requisitions yet</h3>
              <p>
                Click + New Requisition to
                submit a request
              </p>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10
              }}
            >
              {filtered.map(request => (
                <div
                  key={request.id}
                  style={{
                    background: '#fff',
                    border:
                      '1px solid var(--border-light)',
                    borderRadius: 10,
                    padding: 16,
                    cursor: 'pointer'
                  }}
                  onClick={() =>
                    setSelected(request)
                  }
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent:
                        'space-between',
                      alignItems:
                        'flex-start'
                    }}
                  >
                    <div style={{flex: 1}}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginBottom: 4
                        }}
                      >
                        <span
                          style={{
                            fontFamily:
                              'monospace',
                            fontSize: 11,
                            color: '#8A8A8A'
                          }}
                        >
                          {
                            request
                              .requisition_number
                          }
                        </span>

                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: 20,
                            textTransform:
                              'uppercase',
                            ...(
                              STATUS_COLORS[
                                request.status
                              ] || {}
                            )
                          }}
                        >
                          {request.status}
                        </span>

                        {request.priority !==
                          'normal' && (
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              padding:
                                '2px 8px',
                              borderRadius: 20,
                              background:
                                request.priority ===
                                'urgent'
                                  ? '#FDEAEA'
                                  : request.priority ===
                                      'high'
                                    ? '#FFF3DC'
                                    : '#F2EFE7',
                              color:
                                request.priority ===
                                'urgent'
                                  ? '#8F2E24'
                                  : request.priority ===
                                      'high'
                                    ? '#8B5E0A'
                                    : '#556B2F',
                              textTransform:
                                'uppercase'
                            }}
                          >
                            {request.priority}
                          </span>
                        )}
                      </div>

                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: '#3B2A21',
                          marginBottom: 4
                        }}
                      >
                        {request.title}
                      </div>

                      <div
                        style={{
                          fontSize: 11,
                          color: '#8A8A8A'
                        }}
                      >
                        By{' '}
                        {
                          request
                            .submitted_by_name
                        }
                        {' · '}
                        {new Date(
                          request.created_at
                        ).toLocaleDateString(
                          'en-NG'
                        )}

                        {request.total_estimated >
                          0 && (
                          <span>
                            {' · Est: '}
                            {fmt(
                              request
                                .total_estimated
                            )}
                          </span>
                        )}

                        {request.amount_approved >
                          0 && (
                          <span
                            style={{
                              color: '#2E6B3A'
                            }}
                          >
                            {' · Approved: '}
                            {fmt(
                              request
                                .amount_approved
                            )}
                          </span>
                        )}
                      </div>
                    </div>

                    {request.receipt_url && (
                      <span
                        style={{fontSize: 18}}
                        title="Receipt uploaded"
                      >
                        🧾
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {showForm && (
            <RequisitionForm
              onClose={() =>
                setShowForm(false)
              }
              onSaved={() => {
                setShowForm(false);
                load();
              }}
            />
          )}

          {selected && (
            <RequisitionDetail
              req={selected}
              isSuperAdmin={
                isSuperAdmin
              }
              user={user}
              onClose={() =>
                setSelected(null)
              }
              onUpdated={() => {
                setSelected(null);
                load();
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

function RequisitionForm({ onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    title:'', description:'', priority:'normal', notes:''
  });
  const [items, setItems] = useState([{ description:'', quantity:1, unit:'', estimated_cost:'' }]);
  const [saving, setSaving] = useState(false);

  const updateItem = (i, field, val) => {
    const updated = [...items];
    updated[i] = {...updated[i], [field]: val};
    setItems(updated);
  };

  const addItem = () => setItems([...items, { description:'', quantity:1, unit:'', estimated_cost:'' }]);
  const removeItem = (i) => setItems(items.filter((_,idx)=>idx!==i));

  const totalEstimated = items.reduce((sum,item) => sum+(parseFloat(item.estimated_cost||0)*parseFloat(item.quantity||1)), 0);

  const submit = async () => {
    if (!form.title) { alert('Title is required'); return; }
    if (!items[0].description) { alert('Add at least one item'); return; }
    setSaving(true);
    try {
      await post('/requisitions', { ...form, items });
      onSaved();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:600}}>
        <div className="modal-hdr">
          <span className="modal-title">New Requisition</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group" style={{flex:2}}>
              <label className="form-label">Title *</label>
              <input className="form-control" value={form.title}
                onChange={e=>setForm({...form,title:e.target.value})}
                placeholder="e.g. Office supplies for August" />
            </div>
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select className="form-control" value={form.priority}
                onChange={e=>setForm({...form,priority:e.target.value})}>
                {PRIORITIES.map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-control" rows={2} value={form.description}
              onChange={e=>setForm({...form,description:e.target.value})}
              placeholder="Why is this needed?" />
          </div>

          {/* Items */}
          <div style={{marginBottom:16}}>
            <div style={{fontSize:10,fontWeight:600,color:'#556B2F',textTransform:'uppercase',
              letterSpacing:'.08em',marginBottom:8}}>Items</div>
            {items.map((item,i)=>(
              <div key={i} style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr auto',
                gap:8,marginBottom:8,alignItems:'center'}}>
                <input className="form-control" value={item.description}
                  onChange={e=>updateItem(i,'description',e.target.value)}
                  placeholder="Item description *" style={{fontSize:12}} />
                <input type="number" className="form-control" value={item.quantity}
                  onChange={e=>updateItem(i,'quantity',e.target.value)}
                  placeholder="Qty" min="1" style={{fontSize:12}} />
                <input className="form-control" value={item.unit}
                  onChange={e=>updateItem(i,'unit',e.target.value)}
                  placeholder="Unit" style={{fontSize:12}} />
                <input type="number" className="form-control" value={item.estimated_cost}
                  onChange={e=>updateItem(i,'estimated_cost',e.target.value)}
                  placeholder="Est. cost" min="0" style={{fontSize:12}} />
                {items.length > 1 && (
                  <button className="btn btn-ghost btn-sm" style={{color:'#8F2E24',padding:'4px 8px'}}
                    onClick={()=>removeItem(i)}>✕</button>
                )}
              </div>
            ))}
            <button className="btn btn-outline btn-sm" onClick={addItem}>+ Add Item</button>
            {totalEstimated > 0 && (
              <div style={{marginTop:8,fontSize:13,fontWeight:600,color:'#556B2F',textAlign:'right'}}>
                Total Estimated: {fmt(totalEstimated)}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={form.notes}
              onChange={e=>setForm({...form,notes:e.target.value})}
              placeholder="Any additional notes for the Managing Director" />
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving?'Submitting...':'Submit to Managing Director'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RequisitionDetail({ req, isSuperAdmin, user, onClose, onUpdated }) {
  const [approveForm, setApproveForm] = useState({ payment_source:'Petty Cash', amount_approved: req.total_estimated||'', notes:'' });
  const [rejectReason, setRejectReason] = useState('');
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [actualAmount, setActualAmount] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const items = typeof req.items === 'string' ? JSON.parse(req.items||'[]') : (req.items||[]);

  const approve = async () => {
    if (!approveForm.payment_source || !approveForm.amount_approved) {
      alert('Payment source and amount required'); return;
    }
    setSaving(true);
    try {
      await put('/requisitions/'+req.id+'/approve', approveForm);
      onUpdated();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const reject = async () => {
    if (!rejectReason) { alert('Rejection reason required'); return; }
    setSaving(true);
    try {
      await put('/requisitions/'+req.id+'/reject', { rejection_reason: rejectReason });
      onUpdated();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const uploadReceipt = async () => {
    if (!receiptFile) { alert('Please select a file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('receipt', receiptFile);
      if (actualAmount) fd.append('actual_amount', actualAmount);
      const token = localStorage.getItem('bb_token');
      const res = await fetch('/api/requisitions/'+req.id+'/receipt', {
        method:'POST', headers:{ Authorization:'Bearer '+token }, body:fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      onUpdated();
    } catch(err) { alert(err.message); }
    finally { setUploading(false); }
  };

  const deleteReq = async () => {
    if (!window.confirm('Delete this requisition?')) return;
    try {
      await del('/requisitions/'+req.id);
      onUpdated();
    } catch(err) { alert(err.message); }
  };

  const sc = STATUS_COLORS[req.status] || {};

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:580}}>
        <div className="modal-hdr">
          <div>
            <span className="modal-title">{req.requisition_number}</span>
            <span style={{marginLeft:10,fontSize:9,fontWeight:700,padding:'2px 8px',
              borderRadius:20,textTransform:'uppercase',...sc}}>{req.status}</span>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          <h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.2rem',
            color:'#3B2A21',marginBottom:4}}>{req.title}</h3>
          <div style={{fontSize:11,color:'#8A8A8A',marginBottom:12}}>
            Submitted by {req.submitted_by_name} · {new Date(req.created_at).toLocaleDateString('en-NG')}
            {req.priority !== 'normal' && <span style={{marginLeft:8,fontWeight:600,
              color:req.priority==='urgent'?'#8F2E24':'#8B5E0A'}}>· {req.priority.toUpperCase()}</span>}
          </div>

          {req.description && (
            <div style={{background:'#F2EFE7',borderRadius:8,padding:12,marginBottom:12,fontSize:13,color:'#3B2A21'}}>
              {req.description}
            </div>
          )}

          {/* Items */}
          {items.length > 0 && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:600,color:'#556B2F',textTransform:'uppercase',
                letterSpacing:'.08em',marginBottom:8}}>Items</div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'#F2EFE7'}}>
                  {['Description','Qty','Unit','Est. Cost'].map(h=>(
                    <th key={h} style={{padding:'6px 10px',textAlign:'left',fontSize:10,color:'#556B2F'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {items.map((item,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid #F2EFE7'}}>
                      <td style={{padding:'6px 10px'}}>{item.description}</td>
                      <td style={{padding:'6px 10px'}}>{item.quantity}</td>
                      <td style={{padding:'6px 10px',color:'#8A8A8A'}}>{item.unit}</td>
                      <td style={{padding:'6px 10px'}}>
                        {item.estimated_cost ? fmt(parseFloat(item.estimated_cost)*parseFloat(item.quantity||1)) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {req.total_estimated > 0 && (
                <div style={{textAlign:'right',fontSize:13,fontWeight:600,color:'#556B2F',marginTop:8}}>
                  Total Estimated: {fmt(req.total_estimated)}
                </div>
              )}
            </div>
          )}

          {/* Approval info */}
          {req.status === 'approved' && (
            <div style={{background:'#E8F5EB',border:'1px solid #B8DEC0',borderRadius:8,padding:12,marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:600,color:'#2E6B3A',marginBottom:4}}>✅ Approved</div>
              <div style={{fontSize:12,color:'#3B2A21'}}>
                Amount: <strong>{fmt(req.amount_approved)}</strong> from <strong>{req.payment_source}</strong>
              </div>
              {req.approved_at && <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>
                on {new Date(req.approved_at).toLocaleDateString('en-NG')}
              </div>}
            </div>
          )}

          {/* Rejection info */}
          {req.status === 'rejected' && (
            <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,padding:12,marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:600,color:'#8F2E24',marginBottom:4}}>❌ Rejected</div>
              <div style={{fontSize:12,color:'#3B2A21'}}>{req.rejection_reason}</div>
            </div>
          )}

          {/* Receipt upload (approved requisitions) */}
          {req.status === 'approved' && !req.receipt_url && (
            <div style={{background:'#FFF3DC',border:'1px solid #F5C842',borderRadius:8,padding:12,marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:600,color:'#8B5E0A',marginBottom:8}}>🧾 Upload Receipt</div>
              <div className="form-group">
                <label className="form-label">Actual Amount Spent (₦)</label>
                <input type="number" className="form-control" value={actualAmount}
                  onChange={e=>setActualAmount(e.target.value)} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label className="form-label">Receipt / Invoice</label>
                <input type="file" className="form-control" accept=".jpg,.jpeg,.png,.pdf"
                  onChange={e=>setReceiptFile(e.target.files[0])} />
              </div>
              <button className="btn btn-primary btn-sm" onClick={uploadReceipt} disabled={uploading}>
                {uploading?'Uploading...':'Upload Receipt'}
              </button>
            </div>
          )}

          {/* Receipt uploaded */}
          {req.receipt_url && (
            <div style={{background:'#EDF4FF',border:'1px solid #B0C8F0',borderRadius:8,padding:12,marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:600,color:'#1E4FAD',marginBottom:4}}>🧾 Receipt Uploaded</div>
              {req.actual_amount && <div style={{fontSize:12,color:'#3B2A21'}}>
                Actual amount: <strong>{fmt(req.actual_amount)}</strong>
              </div>}
              <a href={req.receipt_url} target="_blank" rel="noreferrer"
                className="btn btn-outline btn-sm" style={{marginTop:8,fontSize:11}}>
                View Receipt
              </a>
            </div>
          )}

          {/* Approve form */}
          {isSuperAdmin && req.status === 'pending' && showApprove && (
            <div style={{background:'#E8F5EB',border:'1px solid #B8DEC0',borderRadius:8,padding:12,marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:600,color:'#2E6B3A',marginBottom:8}}>Approve Requisition</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Payment Source *</label>
                  <select className="form-control" value={approveForm.payment_source}
                    onChange={e=>setApproveForm({...approveForm,payment_source:e.target.value})}>
                    {PAYMENT_SOURCES.map(p=><option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Amount to Approve (₦) *</label>
                  <input type="number" className="form-control" value={approveForm.amount_approved}
                    onChange={e=>setApproveForm({...approveForm,amount_approved:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes (optional)</label>
                <input className="form-control" value={approveForm.notes}
                  onChange={e=>setApproveForm({...approveForm,notes:e.target.value})}
                  placeholder="Any instructions for the requester" />
              </div>
              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-outline btn-sm" onClick={()=>setShowApprove(false)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={approve} disabled={saving}
                  style={{background:'#2E6B3A',border:'none'}}>
                  {saving?'Approving...':'Confirm Approval'}
                </button>
              </div>
            </div>
          )}

          {/* Reject form */}
          {isSuperAdmin && req.status === 'pending' && showReject && (
            <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,padding:12,marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:600,color:'#8F2E24',marginBottom:8}}>Reject Requisition</div>
              <div className="form-group">
                <label className="form-label">Reason *</label>
                <textarea className="form-control" rows={2} value={rejectReason}
                  onChange={e=>setRejectReason(e.target.value)}
                  placeholder="Why is this being rejected?" />
              </div>
              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-outline btn-sm" onClick={()=>setShowReject(false)}>Cancel</button>
                <button className="btn btn-danger btn-sm" onClick={reject} disabled={saving}>
                  {saving?'Rejecting...':'Confirm Rejection'}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="modal-ftr">
          {req.status === 'pending' && req.submitted_by === user?.id && (
            <button className="btn btn-ghost btn-sm" style={{color:'#8F2E24'}} onClick={deleteReq}>
              Delete
            </button>
          )}
          <div style={{flex:1}}></div>
          {isSuperAdmin && req.status === 'pending' && !showApprove && !showReject && (
            <>
              <button className="btn btn-outline btn-sm" style={{color:'#8F2E24',borderColor:'#8F2E24'}}
                onClick={()=>{setShowReject(true);setShowApprove(false);}}>✗ Reject</button>
              <button className="btn btn-primary btn-sm" style={{background:'#2E6B3A',border:'none'}}
                onClick={()=>{setShowApprove(true);setShowReject(false);}}>✓ Approve</button>
            </>
          )}
          <button className="btn btn-outline" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
